import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoggedInUser, useFeatureFlags } from '@tcc/shared-data-lib';
import { useHistory, useLocation } from 'react-router-dom';
import lift from '@tcc/load-lifter';
import { getMessage } from '@tcc/i18n';
import { importFromS3 } from '../../utils/importFromS3';
import { useUserLoggedInConfigs, useFeatureFlagsConfigs } from '../../utils/sharedData';
import { useNavigationItems } from '../../hooks/useNavigationLinks/useNavigationItems';
import { useNavFooterItems } from '../../hooks/useNavFooterItems';
import { Operation } from '../../hooks/useOperations/types';
import { ALL_OPERATIONS } from '../../utils/operations';
import { shouldHideAllOperations } from '../../utils/shouldHideAllOperations';
import { shouldShowOperationSelector } from '../../utils/shouldShowOperationSelector';
import { shouldShowMultiOperationSelector } from '../../utils/shouldShowMultiOperationSelector';
import { toggleOperationSelectionMode } from '../../utils/toggleOperationSelectionMode';
import { getOperationSelectorDisplayText } from '../../utils/getOperationSelectorDisplayText';
import { HermesLibrary } from '../../types/Hermes';
import { useUserSubMenu } from '../../hooks/useUserSubMenu';
import { useSideNavFirstTour } from '../../hooks/useSideNavFirstTour';


interface IProps {
  expanded: boolean;
  operations: Operation[];
  operationsSelected: Operation[];
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
}

const S3Plugin = React.lazy(() => importFromS3('information-architecture'));

export const InfoArchitecturePluginContainer: React.FC<IProps> = ({
  expanded,
  operations,
  operationsSelected,
  setExpandedState,
  setSelectedOperations,
}: IProps) => {
  const { navigationItems } = useNavigationItems();
  const { user, user: { details } } = useLoggedInUser(useUserLoggedInConfigs);
  const { featureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const useModuleFederation = featureFlags['web.sideNav.module-federation'];

  const InformationArchitecturePlugin = useMemo(() => {
    if (useModuleFederation) {
      return React.lazy(() => import('informationArchitecture/InformationArchitecture'));
    }
    return S3Plugin;
  }, [useModuleFederation]);

  const [areNotificationsLoading, setNotificationsLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();
  useSideNavFirstTour();

  const hideAllOperations = useMemo(() => shouldHideAllOperations(location.pathname), [location.pathname]);
  const filteredOperations = useMemo(() =>
    hideAllOperations
      ? operations.filter(op => op.operationId !== ALL_OPERATIONS)
      : operations,
  [hideAllOperations, operations]
  );

  useEffect(() => {
    if (user && operationsSelected.length > 0) {
      lift(['tcc-hermes'], (Hermes: HermesLibrary) => {
        setNotificationsLoading(false);
        Hermes.install(
          null,
          {
            containerSelector: '#hermesButton',
            insideDashboard: false,
          },
          user.details,
          operationsSelected[0].operationId
        );
      });
    }
  }, [user, operationsSelected]);

  const userInfo = {
    fullName: `${details?.firstName} ${details?.lastName}`,
    uuid: details?.uuid,
    email: details?.email,
    avatar: `${details?.firstName?.charAt(0)}${details?.lastName?.charAt(0)}`,
  };
  useMemo(() =>
    shouldShowMultiOperationSelector(location.pathname, featureFlags),
  [location.pathname, featureFlags]
  ); const hasOperationDropdown = shouldShowOperationSelector({ user, route: location.pathname }) && filteredOperations.length !== 0;

  const hasMultiOperationEnabled = shouldShowMultiOperationSelector(location.pathname, featureFlags);


  useEffect(() => {
    toggleOperationSelectionMode({
      hasMultiOperationEnabled,
      operationsSelected,
      operations,
      setSelectedOperations,
      hideAllOperations,
    });
    // Only run when these change
  }, [hasMultiOperationEnabled, operationsSelected, setSelectedOperations, hideAllOperations, operations]);

  const operationsSelectedDisplayMessage = getOperationSelectorDisplayText({
    operations,
    operationsSelected
  });

  const subMenus = useUserSubMenu({ featureFlags, user });

  const navFooterItems = useNavFooterItems({
    expanded,
    setExpandedState,
    countryCode: details?.countryCode,
    featureFlags,
  });

  const onLogoClick = useCallback(() => {
    history.push(`/${location.search}`);
  }, [history, location]);

  return (
    <InformationArchitecturePlugin
      allOperationsDisplayMessage={getMessage('sidenav.allOperations')}
      areNotificationsLoading={areNotificationsLoading}
      expanded={expanded}
      hasOperationDropdown={hasOperationDropdown}
      hasMultiOperationEnabled={hasMultiOperationEnabled}
      navigationItems={navigationItems}
      navFooterItems={navFooterItems}
      onLogoClick={onLogoClick}
      operations={filteredOperations}
      operationsSelected={operationsSelected}
      operationsSelectedDisplayMessage={operationsSelectedDisplayMessage}
      setExpandedState={setExpandedState}
      setSelectedOperations={setSelectedOperations}
      subMenusUserProfile={subMenus}
      userInfo={userInfo}
    />
  );
};
